import { Column } from '@tanstack/react-table';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import {
  BmweColumnMeta,
  BmweDataTypes,
  DateRangeValue,
  NumberRangeValue,
} from '../../types';
import { Dropdown } from 'primereact/dropdown';
import { commonStrings } from '../../i18n';
import DatePicker from '../datePicker/datePicker';
import { toDate } from '../../helper';
import { tableStrings } from './i18n/de-de';
import styles from './bmweDataTable.module.css';

interface BmweTableFilterProps<T> {
  column: Column<T>;
  value?: unknown;
  onChange?: (value: unknown) => void;
}

export function BmweTableFilter<T>({
  column,
  value: externalValue,
  onChange: externalOnChange,
}: BmweTableFilterProps<T>) {
  const columnFilterValue =
    externalValue !== undefined ? externalValue : column.getFilterValue();
  const handleChange =
    externalOnChange ?? ((val: unknown) => column.setFilterValue(val));

  const meta = column.columnDef.meta as BmweColumnMeta | undefined;
  const dataType = meta?.filterType ?? meta?.dataType;
  const columnId = column.id;
  const columnName = String(column.columnDef.header || columnId);

  const getFilter = (dataType: BmweDataTypes | undefined) => {
    switch (dataType) {
      case 'date':
        return (
          <DatePicker
            value={columnFilterValue as Date | undefined}
            onChange={(value) => handleChange(toDate(value))}
            aria-label={tableStrings.messages.filterBy(columnName)}
          />
        );
      case 'dateRange': {
        const rangeValue = (columnFilterValue as DateRangeValue) ?? {};
        return (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.from}
              </span>
              <DatePicker
                value={rangeValue.from}
                onChange={(value) =>
                  handleChange({ ...rangeValue, from: value || undefined })
                }
                aria-label={`${tableStrings.filter.from} - ${columnName}`}
              />
            </label>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.to}
              </span>
              <DatePicker
                value={rangeValue.to}
                onChange={(value) =>
                  handleChange({ ...rangeValue, to: value || undefined })
                }
                aria-label={`${tableStrings.filter.to} - ${columnName}`}
              />
            </label>
          </div>
        );
      }
      case 'numberRange': {
        const numRange = (columnFilterValue as NumberRangeValue) ?? {};
        return (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.from}
              </span>
              <InputText
                className={styles.filterInput}
                value={numRange.min?.toString() ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const num = val === '' ? undefined : Number(val);
                  handleChange({
                    ...numRange,
                    min: Number.isNaN(num) ? numRange.min : num,
                  });
                }}
                keyfilter="num"
                aria-label={`${tableStrings.filter.from} - ${columnName}`}
              />
            </label>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.to}
              </span>
              <InputText
                className={styles.filterInput}
                value={numRange.max?.toString() ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const num = val === '' ? undefined : Number(val);
                  handleChange({
                    ...numRange,
                    max: Number.isNaN(num) ? numRange.max : num,
                  });
                }}
                keyfilter="num"
                aria-label={`${tableStrings.filter.to} - ${columnName}`}
              />
            </label>
          </div>
        );
      }
      case 'currency': {
        const currRange = (columnFilterValue as NumberRangeValue) ?? {};
        return (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.from}
              </span>
              <InputNumber
                className={styles.filterInput}
                value={currRange.min ?? null}
                onValueChange={(e) =>
                  handleChange({
                    ...currRange,
                    min: e.value ?? 0,
                  })
                }
                mode="currency"
                currency="EUR"
                locale="de-DE"
                aria-label={`${tableStrings.filter.from} - ${columnName}`}
              />
            </label>
            <label>
              <span
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  display: 'block',
                }}
              >
                {tableStrings.filter.to}
              </span>
              <InputNumber
                className={styles.filterInput}
                value={currRange.max ?? null}
                onValueChange={(e) =>
                  handleChange({
                    ...currRange,
                    max: e.value ?? 0,
                  })
                }
                mode="currency"
                currency="EUR"
                locale="de-DE"
                aria-label={`${tableStrings.filter.to} - ${columnName}`}
              />
            </label>
          </div>
        );
      }
      case 'boolean':
        return (
          <Dropdown
            value={columnFilterValue ?? 'all'}
            onChange={(e) => {
              const rawValue = e.value;
              const value = typeof rawValue === 'boolean' ? e.value : null;

              handleChange(value);
            }}
            options={[
              {
                name: commonStrings.words.all,
                value: 'all',
              },
              {
                name: commonStrings.words.yes,
                value: true,
              },
              {
                name: commonStrings.words.no,
                value: false,
              },
            ]}
            optionLabel="name"
            aria-label={tableStrings.messages.filterBy(columnName)}
            style={{ width: '100%' }}
          />
        );
      default:
        return (
          <InputText
            className={styles.filterInput}
            value={(columnFilterValue ?? '') as string}
            onChange={(event) => handleChange(event.target.value)}
            placeholder={`Filter ${columnName}`}
            aria-label={tableStrings.messages.filterBy(columnName)}
          />
        );
    }
  };

  return <div>{getFilter(dataType)}</div>;
}
